import React, { useState } from "react";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import FilterListIcon from "@mui/icons-material/FilterList";
import PageHeader from "../common/PageHeader";
import OrderFilters from "./components/OrderFilters";
import Pagination from "../Pagination";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../helper/formatCurrency";
import { useCustomerReport } from "./hooks/useCustomerReport";
import { formatOrderedTime, formatDateForAPI } from "./utils/orderUtils";
import { TABLE_ROWS_PER_PAGE } from "./utils/constants";
import { downloadCustomerReportPDF } from "./utils/pdfUtils";
import OrderActions from "./components/OrderActions";

const CustomerReport = () => {
  const { locale, timezone, restaurantName } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("lastOrderDate");
  const [sortOrder, setSortOrder] = useState(-1);
  const [status, setStatus] = useState("");
  const [orderType, setOrderType] = useState("");
  const [search, setSearch] = useState("");

  const {
    customers,
    totalCustomers,
    totalOrders,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    showAllDates,
    setShowAllDates,
    isLoading,
  } = useCustomerReport();

  const handleDownloadPDF = () => {
    const totalSalesNum = customers.reduce((sum, customer) => sum + (Number(customer.sales) || 0), 0);
    const dateRangeStr = showAllDates
      ? "All Dates"
      : `${(fromDate && formatDateForAPI(fromDate)) || "N/A"} - ${(toDate && formatDateForAPI(toDate)) || "N/A"}`;
      
    downloadCustomerReportPDF(
      customers,
      currencySymbol,
      timezone,
      restaurantName,
      dateRangeStr,
      totalCustomers,
      totalSalesNum
    );
  };

  const sortedCustomers = [...customers].sort((a, b) => {
    const numericFields = ["orders", "sales"];
    const aValue = numericFields.includes(sortField)
      ? Number(a[sortField] || 0)
      : a[sortField];
    const bValue = numericFields.includes(sortField)
      ? Number(b[sortField] || 0)
      : b[sortField];

    if (sortField === "lastOrderDate") {
      const aTime = aValue ? new Date(aValue).getTime() : 0;
      const bTime = bValue ? new Date(bValue).getTime() : 0;
      return (aTime - bTime) * sortOrder;
    }

    const aStr = aValue ?? "";
    const bStr = bValue ?? "";
    if (aStr < bStr) return -1 * sortOrder;
    if (aStr > bStr) return 1 * sortOrder;
    return 0;
  });

  const indexOfLast = currentPage * TABLE_ROWS_PER_PAGE;
  const indexOfFirst = indexOfLast - TABLE_ROWS_PER_PAGE;
  const currentRows = sortedCustomers.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(customers.length / TABLE_ROWS_PER_PAGE);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [customers.length]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder * -1);
    } else {
      setSortField(field);
      setSortOrder(field === "lastOrderDate" ? -1 : 1);
    }
  };

  const SortIndicator = ({ field }) =>
    sortField === field ? (
      <span className="text-xs">{sortOrder === 1 ? "▲" : "▼"}</span>
    ) : null;

  const thClass =
    "px-6 py-5 text-left text-sm font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-gray-800";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 px-2 py-3">
      <PageHeader
        title="Customer Report"
        subtitle={
          <span className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
            View customer order activity and sales summary
          </span>
        }
      />

      {/* Stats Overview Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        {/* Total Customers Card */}
        <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg p-3 shadow-md hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <div className="text-white/90 text-xs font-medium">
              Total Customers
            </div>
          </div>
          <div className="text-white text-lg lg:text-xl font-bold tracking-tight">
            {isLoading ? (
              <span className="animate-pulse">...</span>
            ) : (
              totalCustomers
            )}
          </div>
        </div>

        {/* Total Orders Card */}
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg p-3 shadow-md hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <div className="text-white/90 text-xs font-medium">
              Total Orders
            </div>
          </div>
          <div className="text-white text-lg lg:text-xl font-bold tracking-tight">
            {isLoading ? (
              <span className="animate-pulse">...</span>
            ) : (
              totalOrders
            )}
          </div>
        </div>

        {/* Date Range Card */}
        <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-lg p-3 shadow-md hover:shadow-lg transition-all">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-md flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div className="text-white/90 text-xs font-medium">Date Range</div>
          </div>
          <div className="text-white text-sm lg:text-base font-semibold tracking-tight">
            {showAllDates
              ? "All Dates"
              : `${(fromDate && formatDateForAPI(fromDate)) || "N/A"} - ${(toDate && formatDateForAPI(toDate)) || "N/A"}`}
          </div>
        </div>
      </div>

      {/* Collapsible Filters */}
      <div className="bg-white/80 backdrop-blur-xl rounded-xl shadow-sm border border-gray-200/60 overflow-hidden mb-4 hover:shadow-lg transition-all duration-300">
        <div
          className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-all duration-300 group bg-white"
          onClick={() => setShowFilters(!showFilters)}
        >
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200">
              <FilterListIcon className="text-white w-4 h-4" />
            </div>
            <div className="flex flex-col">
              <span className="font-display font-semibold text-sm text-gray-900 tracking-tight">
                Filters & Actions
              </span>
              <span className="font-text text-xs text-gray-500">
                {showFilters
                  ? "Refine your search criteria"
                  : "Click to customize your view"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-xs font-text text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {customers.length} results
            </div>
            <IconButton
              size="small"
              className="text-gray-500 hover:text-blue-500 transition-all duration-200 hover:bg-blue-50 rounded-lg p-1"
            >
              {showFilters ? (
                <KeyboardArrowUpIcon className="w-4 h-4" />
              ) : (
                <KeyboardArrowDownIcon className="w-4 h-4" />
              )}
            </IconButton>
          </div>
        </div>

        <div
          className={`transition-all duration-500 ease-out overflow-hidden ${showFilters ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
            }`}
        >
          <div className="border-t border-gray-100/60">
            <div className="px-3 pb-3 pt-2 max-h-[350px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <OrderFilters
                fromDate={fromDate}
                setFromDate={setFromDate}
                toDate={toDate}
                setToDate={setToDate}
                showAllDates={showAllDates}
                setShowAllDates={setShowAllDates}
                status={status}
                setStatus={setStatus}
                orderType={orderType}
                setOrderType={setOrderType}
                orderTypeOptions={[]}
                search={search}
                setSearch={setSearch}
                hideNonDateFilters={true}
              />
              <div className="mt-2">
                <OrderActions
                  onDownloadPDF={handleDownloadPDF}
                  hideSummary={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-900 to-black px-6 py-4 flex items-center justify-between border-b-2 border-white">
          <h3 className="text-white text-lg font-semibold">Customer Report</h3>
        </div>

        {isLoading ? (
          <div className="px-6 py-16 text-center">
            <div className="animate-pulse flex flex-col items-center justify-center">
              <svg
                className="w-12 h-12 text-gray-300 mb-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <p className="text-gray-500 font-medium text-lg">
                Loading customers...
              </p>
            </div>
          </div>
        ) : currentRows.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="text-gray-400 mb-3">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <p className="text-gray-500 text-lg font-medium">
              No customers available
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Try adjusting your date filters
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-900 to-black sticky top-0 z-10">
                <tr>
                  <th
                    className={thClass}
                    onClick={() => handleSort("customerName")}
                  >
                    <div className="flex items-center gap-2">
                      Customer Name
                      <SortIndicator field="customerName" />
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort("mobile")}
                  >
                    <div className="flex items-center gap-2">
                      Mobile
                      <SortIndicator field="mobile" />
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort("orders")}
                  >
                    <div className="flex items-center gap-2">
                      Orders
                      <SortIndicator field="orders" />
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort("sales")}
                  >
                    <div className="flex items-center gap-2">
                      Sales
                      <SortIndicator field="sales" />
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort("lastOrderDate")}
                  >
                    <div className="flex items-center gap-2">
                      Last Order Date
                      <SortIndicator field="lastOrderDate" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {currentRows.map((customer, index) => (
                  <tr
                    key={`${customer.mobile || customer.customerName}-${index}`}
                    className="h-[70px] hover:bg-gradient-to-r hover:from-gray-50 hover:to-white transition-all duration-200 hover:shadow-sm"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-gray-900 text-base">
                        {customer.customerName || "Guest"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-600 text-sm font-medium">
                        {customer.mobile || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-gray-700 text-sm font-medium">
                        {customer.orders ?? 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-semibold text-green-600 text-base">
                        {currencySymbol}
                        {Number(customer.sales || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-600 font-medium">
                        {formatOrderedTime(customer.lastOrderDate, timezone)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {customers.length > TABLE_ROWS_PER_PAGE && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default CustomerReport;
